import React from "react";
import { toast } from "react-toastify";
import { Button, Modal } from "@/Components/Common/Tailwind";
import { API, SITE_URL } from "../../api";
import GiftPhoneNumList from "../../pages/UserPage/purchaseList/GiftPhoneNumList.jsx";
import { getAccessToken } from "../../api/auth";
import { shareKakao } from "../../common/social/kakao/api";
import { handleCopyClipBoard } from "../../common/utils/clipboard";
import {
  buildShareUrl,
  getGiftSendErrorMessage,
  getRequiredResponseValue,
} from "../../common/social/kakao/giftShare.mjs";

type UserVoucherInfo = {
  id: string | number;
  name: string;
  expire_date: string;
  state: string;
  image?: string;
};

interface UserVoucherGiftModalProps {
  infos: UserVoucherInfo[];
  setInfos: (value: UserVoucherInfo[]) => void;
  visible: boolean;
  setVisible: (value: boolean) => void;
  needRefresh: () => void;
}

const UserVoucherGiftModal: React.FC<UserVoucherGiftModalProps> = ({
  infos,
  setInfos,
  visible,
  setVisible,
  needRefresh,
}) => {
  const togModal = () => setVisible(!visible);
  const [giftShareUrl, setGiftShareUrl] = React.useState("");
  const [isSharing, setIsSharing] = React.useState(false);

  React.useEffect(() => {
    if (!visible) {
      setGiftShareUrl("");
      setIsSharing(false);
    }
  }, [visible, infos?.[0]?.id]);

  const inviteUserForVoucher = async (userVoucherId: UserVoucherInfo["id"], phoneNum: string) => {
    const phone_num = phoneNum.split("-").join("");
    const accessToken = getAccessToken();

    try {
      const response = await API.post(
        "/voucher/invite-user-for-gift/",
        {
          userVoucherId: userVoucherId,
          phoneNum: phone_num,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = response.data;

      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const giftUserVoucherByUrl = async (userVoucherId: UserVoucherInfo["id"]) => {
    const accessToken = getAccessToken();

    try {
      const response = await API.post(
        "/voucher/send-user-voucher-gift-url/",
        {
          userVoucherId: userVoucherId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = response.data;

      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleGiftUrlShare = async () => {
    if (isSharing || !infos?.length) return;

    setIsSharing(true);
    try {
      let shareUrl = giftShareUrl;

      if (!shareUrl) {
        const data = await giftUserVoucherByUrl(infos[0].id);
        const giftId = getRequiredResponseValue(data, "gift_id");
        shareUrl = buildShareUrl(SITE_URL, ["gift", "voucher", giftId]);
        setGiftShareUrl(shareUrl);
        needRefresh();
      }

      const opened = shareKakao({
        title: `${infos[0].name}`,
        description: "교환권을 선물 받았습니다.",
        imageUrl: infos[0].image ? infos[0].image : "",
        shareUrl,
      });

      if (opened) {
        toast.success("카카오톡 공유창을 열었습니다.");
        setInfos([]);
        setVisible(false);
        return;
      }

      await handleCopyClipBoard(shareUrl, {
        successMessage: "카카오톡 공유를 열 수 없어 링크를 복사했어요.",
        errorMessage: "카카오톡 공유를 열 수 없습니다. 링크를 복사해 주세요.",
      });
    } catch (error) {
      console.error(error);
      toast.error(getGiftSendErrorMessage(error, "선물하기에 실패했습니다."));
    } finally {
      setIsSharing(false);
    }
  };


  if (!infos?.length) return null;

  return (
    <React.Fragment>
      <Modal toggle={togModal} isOpen={visible} centered>
        <div>
          <div className="mb-4 flex justify-center">
            <h4 className="text-lg font-semibold">교환권 선물하기</h4>
          </div>
          {infos.length === 1 ? (
            <div>
              <ul className="list-group">
                <li className="list-group-item">ID : {infos[0].id}</li>
                <li className="list-group-item">상품 : {infos[0].name}</li>
                <li className="list-group-item">만기일 : {infos[0].expire_date}</li>
                <li className="list-group-item">상태 : {infos[0].state}</li>
              </ul>

              <div style={{ marginTop: "24px" }}></div>
              <div className="flex justify-center mx-1 mb-4">
                <Button
                  variant="primary"
                  className="gap-2 btn-kakao"
                  disabled={isSharing}
                  onClick={handleGiftUrlShare}
                >
                  <i className="ri-gift-fill"></i>
                  {isSharing ? "공유 준비중..." : "카카오톡으로 선물보내기"}
                </Button>
              </div>

              {giftShareUrl ? (
                <div className="mx-1 mb-4">
                  <label className="mb-1 block text-sm text-gray-500" htmlFor="voucherGiftShareUrl">
                    공유 링크
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="voucherGiftShareUrl"
                      readOnly
                      value={giftShareUrl}
                      className="h-9 min-w-0 flex-1 rounded border border-gray-300 px-2 text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isSharing}
                      onClick={() => handleCopyClipBoard(giftShareUrl)}
                    >
                      복사
                    </Button>
                  </div>
                </div>
              ) : null}


            </div>
          ) : (
            <div style={{ height: "20vh", overflowY: "scroll" }}>
              <ul className="list-group">
                {infos.map((info, idx) => {
                  return (
                    <li key={idx} className="list-group-item">
                      ID: {info.id} 상품: {info.name} 만기일: {info.expire_date}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div style={{ marginTop: "24px" }}></div>
          <GiftPhoneNumList
            sendItem={inviteUserForVoucher}
            info={infos[0]}
            setInfo={() => {
              setInfos([]);
            }}
            {...{ visible, setVisible, needRefresh }}
          />
        </div>
      </Modal>
    </React.Fragment>
  );
};
export default UserVoucherGiftModal;
